/**
 * BUG-083 — the drawer answered every 409 with the same wrong sentence.
 *
 * Approving a LinkedIn amplification showed:
 *
 *   "This post changed since you opened it. Close and reopen the drawer to
 *    continue."
 *
 * Nothing had changed, and closing the drawer could never help. The server had
 * said something precise and actionable:
 *
 *   "a LinkedIn article post is published with its article, not scheduled —
 *    it fills the Create post modal when the article goes out"
 *
 * That is a deliberate rule (FEAT-086 / S-386): the companion post goes out
 * with its article through the browser service, so scheduling it on its own is
 * how row 868 stranded. The drawer threw the explanation away and replaced it
 * with advice that leads nowhere.
 *
 * `/social-calendar/:id/*` returns 409 for two very different things, and only
 * one of them is a race:
 *
 *   error: "conflict"            someone else changed the row — reopen and retry
 *   error: "invalid_transition"  this action is not legal here, and never will
 *                                be for this row. Reopening changes nothing.
 *
 * Same discipline as `describeRunError`, which already differentiates the 409
 * shapes on POST /pieces/:id/run.
 */
import { ApiError } from "@/lib/api-error";

const STALE =
  "This post changed since you opened it. Close and reopen the drawer to continue.";

/**
 * What to show the operator when a social-calendar action fails.
 *
 * `fallback` is the wording for anything unrecognised — "Approve failed.",
 * "Schedule failed." — so the caller keeps naming its own action.
 */
export function describeSocialActionError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return (err as { message?: string })?.message ?? fallback;
  }

  if (err.status === 409) {
    const parsed = parseBody(err.body);

    // A real race: the row moved underneath this drawer. Reopening is the fix,
    // so this is the one case where that sentence is true.
    if (parsed?.error === "conflict") return STALE;

    // Anything else at 409 is a rule, not a race. The server's own wording is
    // better than anything that can be reconstructed here — it knows which
    // rule and why.
    if (parsed?.message) return parsed.message;

    // A 409 with no readable body. Do not claim it is staleness; that is the
    // guess that caused this bug.
    return fallback;
  }

  if (err.status === 401) return "Not signed in.";
  if (err.status === 403) return "You don't have permission to do that.";

  // BUG-096 — the provider refused the post. Its reason is the useful part,
  // and "try again" never helps a refusal. Narrow on purpose: only the
  // dispatch routes' own shape, so no other 5xx body reaches the screen.
  if (err.status === 502) {
    const parsed = parseBody(err.body);
    if (parsed?.error === "dispatch_failed" && typeof parsed.message === "string" && parsed.message) {
      return `Couldn't publish: ${parsed.message}`;
    }
    // BUG-102 — the provider refused to delete a handed-off post.
    if (parsed?.error === "cancel_failed" && typeof parsed.message === "string" && parsed.message) {
      return `Couldn't cancel: ${parsed.message}`;
    }
  }

  if (err.status >= 500) return "Server error — please try again.";

  // FEAT-102 / BUG-101 — a refused save. The image picker's 422
  // (image_not_allowed) carries a message, handled below; a validation 422
  // carries `details` instead. Either way, never the raw JSON.
  if (err.status === 422) {
    const parsed = parseBody(err.body);
    if (typeof parsed?.message === "string" && parsed.message) return parsed.message;
    const details = parsed?.details;
    if (Array.isArray(details) && details.length > 0 && details.every((d) => typeof d === "string")) {
      return details.join("; ");
    }
    return fallback;
  }

  // The server's own words if it gave any; otherwise the caller's, which names
  // the action. Deliberately NOT err.message — for an empty body ApiError
  // builds "418 API error", and a status code is not something to put in front
  // of someone who just wanted to schedule a post.
  return parseBody(err.body)?.message ?? fallback;
}

function parseBody(body: string | undefined): { error?: string; message?: string; details?: unknown } | null {
  if (!body) return null;
  try {
    const json = JSON.parse(body) as { error?: string; message?: string; details?: unknown };
    return typeof json === "object" && json !== null ? json : null;
  } catch {
    return null;
  }
}

/**
 * Does this platform publish with its article rather than on a schedule?
 *
 * The drawer should not offer Schedule for one of these at all — being told
 * "no" after clicking is better than the old message, but not being offered an
 * action that cannot succeed is better still.
 */
export function publishesWithItsArticle(platform: string | null | undefined): boolean {
  return platform === "linkedin_article";
}
